import {
  Check,
  Clear,
  EllipsisVertical,
  Error,
  Info,
  Warning,
} from '@procore/core-icons/dist'
import { useId } from '@react-aria/utils'
import { partition } from 'ramda'
import React from 'react'
import { Button } from '../Button'
import { Card } from '../Card'
import { Menu, useMenu } from '../Menu/Menu'
import { OverlayTrigger } from '../OverlayTrigger'
import { useI18nContext } from '../_hooks/I18n'
import { OverflowObserver } from '../_hooks/OverflowObserver'
import { addSubcomponents } from '../_utils/addSubcomponents'
import { mergeRefs } from '../_utils/mergeRefs'
import type { Props } from '../_utils/types'
import {
  StyledAction,
  StyledActionsTrigger,
  StyledBannerContainer,
  StyledBannerDismiss,
  StyledBannerRow,
  StyledBody,
  StyledContent,
  StyledExpandableContent,
  StyledExpandableContentContainer,
  StyledIconContainer,
  StyledMiniBannerContainer,
  StyledTitle,
} from './Banner.styles'
import type {
  BannerExpandableContentProps,
  BannerExpandActionButtonProps,
  BannerIconProps,
  BannerProps,
  BannerVariant,
} from './Banner.types'

interface BannerA11yContextValue {
  setTitleId: (id: string | undefined) => void
}

const BannerA11yContext = React.createContext<BannerA11yContextValue | null>(
  null
)

const defaultIcons: Record<BannerVariant, React.ReactNode> = {
  error: <Error size="lg" />,
  attention: <Warning size="lg" />,
  info: <Info size="lg" />,
  success: <Check size="lg" />,
  UNSAFE_attention_emphasized: <Warning size="lg" />,
}

interface BannerLabelingArgs {
  hasLabelProvided: boolean
  defaultAriaLabel: string
  role: React.AriaRole | undefined
  titleId: string | undefined
}

interface BannerLabeledNodes {
  landmarkProps: Pick<
    React.HTMLAttributes<HTMLDivElement>,
    'role' | 'aria-label' | 'aria-labelledby'
  >
  // liveRegionProps: Pick<React.HTMLAttributes<HTMLDivElement>, 'role'>
}

function getBannerA11yProps({
  hasLabelProvided,
  defaultAriaLabel,
  role,
  titleId,
}: BannerLabelingArgs): BannerLabeledNodes {
  if (role !== 'region') {
    return {
      landmarkProps: { role },
    }
  }

  if (hasLabelProvided) {
    return {
      landmarkProps: { role: 'region' },
    }
  }

  if (titleId) {
    return {
      landmarkProps: {
        role: 'region',
        'aria-labelledby': titleId,
      },
    }
  }

  return {
    landmarkProps: {
      role: 'region',
      'aria-label': defaultAriaLabel,
    },
  }
}

const Banner_ = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & BannerProps
>(function Banner({ children, role = 'region', variant, ...props }, ref) {
  const I18n = useI18nContext()
  const [titleId, setTitleId] = React.useState<string>()
  const [expandableContentNodes, content] = partition(
    (element: React.ReactNode) =>
      React.isValidElement(element) && element.type === ExpandableContent,
    React.Children.toArray(children)
  ) as [React.ReactElement[], React.ReactElement[]]
  const isExpandable = expandableContentNodes.length !== 0

  const iconElement = React.useMemo(() => {
    return React.Children.toArray(children).find(
      (child) => React.isValidElement(child) && child.type === Icon
    ) as React.ReactElement<BannerIconProps> | undefined
  }, [children])

  const hasCustomIcon = iconElement && iconElement.props.icon
  const defaultAriaLabelByVariant: Record<BannerVariant, string> = {
    error: I18n.t('core.banner.defaultLabels.error'),
    attention: I18n.t('core.banner.defaultLabels.attention'),
    info: I18n.t('core.banner.defaultLabels.info'),
    success: I18n.t('core.banner.defaultLabels.success'),
    UNSAFE_attention_emphasized: I18n.t(
      'core.banner.defaultLabels.unsafeAttentionEmphasized'
    ),
  }

  const { landmarkProps } = getBannerA11yProps({
    hasLabelProvided: Boolean(props['aria-label'] || props['aria-labelledby']),
    defaultAriaLabel: defaultAriaLabelByVariant[variant],
    role,
    titleId,
  })

  const a11yContextValue = React.useMemo(() => ({ setTitleId }), [setTitleId])

  return (
    <BannerA11yContext.Provider value={a11yContextValue}>
      <StyledBannerContainer
        ref={ref}
        $isExpandable={isExpandable}
        $variant={variant}
        {...landmarkProps}
        {...props}
      >
        {isExpandable ? (
          <>
            <StyledExpandableContentContainer
              justifyContent="space-between"
              alignItems="center"
              gap="lg"
            >
              {!hasCustomIcon && <Icon icon={defaultIcons[variant]} />}
              {content}
            </StyledExpandableContentContainer>
            {expandableContentNodes}
          </>
        ) : (
          <StyledBannerRow
            justifyContent="flex-start"
            alignItems="center"
            gap="lg"
            grow="1"
          >
            {!hasCustomIcon && <Icon icon={defaultIcons[variant]} />}
            {content}
          </StyledBannerRow>
        )}
      </StyledBannerContainer>
    </BannerA11yContext.Provider>
  )
})

const Content = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & Props
>(function Content({ children, ...props }, ref) {
  return (
    <StyledContent {...props} ref={ref}>
      {children}
    </StyledContent>
  )
})

const ExpandableContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & BannerExpandableContentProps
>(function ExpandableContent(
  { children, expanded = false, expandableContentMaxHeight = 120, ...props },
  ref
) {
  const containerRef = React.useRef<HTMLDivElement>(null)

  return expanded ? (
    <OverflowObserver>
      {({ ref: overflowRef, isOverflowingY }) => (
        <StyledExpandableContent
          ref={mergeRefs(ref, containerRef)}
          data-qa="core-banner-expandable-content"
          onAnimationEnd={() => overflowRef(containerRef.current)}
          $isOverflowingY={isOverflowingY}
          $expandableContentMaxHeight={expandableContentMaxHeight}
          {...props}
          {...(isOverflowingY && { tabIndex: 0 })}
        >
          {children}
        </StyledExpandableContent>
      )}
    </OverflowObserver>
  ) : null
})

const Title = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  }
>(function Title({ children, ...props }, ref) {
  const generatedId = useId()
  const titleId = props.id ?? generatedId
  const a11yContext = React.useContext(BannerA11yContext)

  React.useEffect(() => {
    a11yContext?.setTitleId(titleId)

    return () => a11yContext?.setTitleId(undefined)
  }, [a11yContext, titleId])

  return (
    <StyledTitle id={titleId} {...props} ref={ref}>
      {children}
    </StyledTitle>
  )
})

const Body = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function Body({ children, ...props }, ref) {
  return (
    <StyledBody {...props} ref={ref}>
      {children}
    </StyledBody>
  )
})

const Action = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function Action({ children, ...props }, ref) {
  return (
    <StyledAction {...props} ref={ref}>
      {children}
    </StyledAction>
  )
})

const ExpandActionButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & BannerExpandActionButtonProps
>(function ExpandActionButton(
  { isExpanded, onExpandChange, children, ...props },
  ref
) {
  const I18n = useI18nContext()
  const label =
    children ??
    (isExpanded ? I18n.t('core.banner.collapse') : I18n.t('core.banner.expand'))

  return (
    <Button
      ref={ref}
      type="button"
      aria-expanded={isExpanded}
      onClick={() => onExpandChange(!isExpanded)}
      {...props}
    >
      {label}
    </Button>
  )
})

const DropdownAction = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function DropdownAction({ children, ...props }, ref) {
  return (
    <Menu.Item ref={ref} item={null} {...(props as any)}>
      {children}
    </Menu.Item>
  )
})

const Actions = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function Actions({ children, ...props }, ref) {
  const actionButtons = React.Children.toArray(children).filter(
    (element: React.ReactNode) =>
      React.isValidElement(element) && element.type === Action
  ) as React.ReactElement[]

  return (
    <OverlayTrigger
      overlay={
        <Card>
          <Menu
            ref={(node) => node?.focus()}
            usingHook={useMenu({
              isSelectable: ({ type }) => type === DropdownAction,
            })}
          >
            <Menu.Options>
              {actionButtons.map(({ props: actionButtonProps }, idx) => (
                <DropdownAction key={idx} {...actionButtonProps} />
              ))}
            </Menu.Options>
          </Menu>
        </Card>
      }
      ref={ref}
      placement="bottom-right"
    >
      <StyledActionsTrigger
        data-qa="core-banner-actions-trigger"
        variant="tertiary"
        arrow={false}
        icon={<EllipsisVertical />}
        {...props}
      />
    </OverlayTrigger>
  )
})

const Icon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & BannerIconProps
>(function Icon({ children, icon, ...props }, ref) {
  return (
    <StyledIconContainer ref={ref} aria-hidden="true" {...props}>
      {icon}
    </StyledIconContainer>
  )
})

const Dismiss = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function Dismiss({ children, ...props }, ref) {
  const I18n = useI18nContext()

  return (
    <StyledBannerDismiss
      aria-label={I18n.t('core.banner.dismissAlert')}
      ref={ref}
      {...props}
      icon={<Clear />}
      variant="tertiary"
    />
  )
})

export const ErrorBanner = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & Omit<BannerProps, 'variant'>
>(function ErrorBanner({ children, ...props }, ref) {
  return (
    <Banner ref={ref} variant="error" {...props}>
      <Icon icon={<Error size="lg" />} />
      {children}
    </Banner>
  )
})

export const InfoBanner = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & Omit<BannerProps, 'variant'>
>(function InfoBanner({ children, ...props }, ref) {
  return (
    <Banner ref={ref} variant="info" {...props}>
      <Icon icon={<Info size="lg" />} />
      {children}
    </Banner>
  )
})

export const SuccessBanner = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & Omit<BannerProps, 'variant'>
>(function SuccessBanner({ children, ...props }, ref) {
  return (
    <Banner ref={ref} variant="success" {...props}>
      <Icon icon={<Check size="lg" />} />
      {children}
    </Banner>
  )
})

/**
 * @since 11.26.0
 */
export const AttentionBanner = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & Omit<BannerProps, 'variant'>
>(function AttentionBanner({ children, ...props }, ref) {
  return (
    <Banner ref={ref} variant="attention" {...props}>
      <Icon icon={<Warning size="lg" />} />
      {children}
    </Banner>
  )
})
/**
 * @deprecated ActionBanner was renamed to AttentionBanner.
 * @deprecatedSince 11.26.0
 */
export const ActionBanner = AttentionBanner

export const UNSAFE_MiniBanner = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { variant?: BannerVariant }
>(function MiniBanner({ children, variant = 'info', ...props }, ref) {
  return (
    <StyledMiniBannerContainer ref={ref} variant={variant} {...props}>
      {children}
    </StyledMiniBannerContainer>
  )
})

Banner_.displayName = 'Banner'

Body.displayName = 'Banner.Body'

Content.displayName = 'Banner.Content'

ExpandableContent.displayName = 'Banner.ExpandableContent'

Title.displayName = 'Banner.Title'

Action.displayName = 'Banner.Action'

ExpandActionButton.displayName = 'Banner.ExpandActionButton'

Actions.displayName = 'Banner.Actions'

DropdownAction.displayName = 'Banner.DropdownAction'

Icon.displayName = 'Banner.Icon'

Dismiss.displayName = 'Banner.Dismiss'

ErrorBanner.displayName = 'ErrorBanner'

InfoBanner.displayName = 'InfoBanner'

SuccessBanner.displayName = 'SuccessBanner'

AttentionBanner.displayName = 'AttentionBanner'

ActionBanner.displayName = 'ActionBanner'

UNSAFE_MiniBanner.displayName = 'MiniBanner'

/**


 We use banners to contextually convey complicated or supplemental information
 to a user. Depending on the type of banner, we can provide more information
 about a feature or page the user is on, an action the user needs to perform,
 or an error the user has encountered.

 Procore banners are informative and actionable. Always explain what the user
 needs to know (and in some cases, why they’re seeing the banner), and provide
 an actionable next step.

 @since 10.19.0

 @see [Storybook](https://stories.core.procore.com/?path=/story/core-react_demos-banner--demo)

 @see [Design Guidelines](https://design.procore.com/banner)

 */
export const Banner = addSubcomponents(
  {
    Body,
    Content,
    ExpandableContent,
    Title,
    Action,
    ExpandActionButton,
    Actions,
    Icon,
    Dismiss,
  },
  Banner_
)
